import useSWR, { type KeyedMutator } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import type { PropertyCodePictureRequestListResponse } from "@/features/propertyCodeAcquisition/types";
import {
  buildPropertyCodePictureRequestEndpoint,
  getPropertyCodePictureRequestList,
} from "@/features/propertyCodeAcquisition/api/getPropertyCodePictureRequestList";
import { PROPERTY_CODE_SWR_OPTIONS } from "@/features/propertyCodeAcquisition/hooks/propertyCodeSwrConfig";

interface UsePropertyCodePictureRequestListReturn {
  history: PropertyCodePictureRequestListResponse | undefined;
  isLoading: boolean;
  /** 再検証中（再取得ボタン押下・mutate 中）。データ保持中の再フェッチ進行を表す。 */
  isValidating: boolean;
  error: Error | undefined;
  /** 取得成功後の履歴再取得に用いる。 */
  mutate: KeyedMutator<PropertyCodePictureRequestListResponse>;
}

/**
 * 取得履歴一覧を取得するカスタムフック。
 * GET /property-codes/{code}/picture-requests（apiClient の baseURL が /api を付与）。
 * BE は全件返却（API ページネーションなし）。テーブルの表示ページングはクライアント側
 * （PropertyCodePictureRequestTable）で行う。
 * @param code 物件コード（UUID）。未確定時は null。
 */
export const usePropertyCodePictureRequestList = (
  code: string | null
): UsePropertyCodePictureRequestListReturn => {
  const { apiClient } = useApiClient();

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    PropertyCodePictureRequestListResponse,
    Error
  >(
    code != null ? buildPropertyCodePictureRequestEndpoint(code) : null,
    // key が非 null（code 非 null）の時のみ fetcher が呼ばれる。fetcher 自体を null/非 null で
    // 出し分けることで、true 枝で code が string に絞り込まれ as キャストが不要になる。
    code != null
      ? async () => await getPropertyCodePictureRequestList(apiClient, code)
      : null,
    PROPERTY_CODE_SWR_OPTIONS
  );

  return { history: data, isLoading, isValidating, error, mutate };
};
